import type { Session, SupabaseClient } from '@supabase/supabase-js';

/**
 * Adopt a fresher session written to the shared `.rehub.studio` cookie by a
 * sibling app, without rotating the refresh token.
 *
 * Cross-subdomain clients each hold their own in-memory session. When one
 * origin refreshes, it writes the new tokens to the shared cookie — but other
 * origins keep the old refresh token in memory and will later try to rotate
 * it, triggering "Already Used" + full session revoke.
 *
 * Call this on visibility/focus and during SIGNED_OUT recovery so a tab that
 * lost its in-memory session can pick up the cookie's still-valid tokens.
 * `setSession` only hits the network when the access token is already expired.
 *
 * Pass `currentRefreshToken` (from React session state) to avoid calling
 * `getSession()` — that races with in-flight storage/RPC fetches and aborts them.
 */
export async function adoptSharedSession(
  client: SupabaseClient,
  currentRefreshToken?: string | null,
): Promise<Session | null> {
  const auth = client.auth as unknown as {
    storageKey?: string;
    storage?: {
      getItem: (key: string) => string | null | Promise<string | null>;
    };
  };

  const key = auth.storageKey;
  const storage = auth.storage;
  if (!key || !storage?.getItem) return null;

  let raw: string | null;
  try {
    raw = await Promise.resolve(storage.getItem(key));
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: Record<string, unknown>;
  try {
    const json = raw.startsWith('base64-') ? atob(raw.slice(7)) : raw;
    parsed = JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }

  const candidate = (parsed.currentSession ?? parsed) as {
    access_token?: unknown;
    refresh_token?: unknown;
  };
  if (
    typeof candidate.access_token !== 'string' ||
    typeof candidate.refresh_token !== 'string' ||
    !candidate.access_token ||
    !candidate.refresh_token
  ) {
    return null;
  }

  if (currentRefreshToken && currentRefreshToken === candidate.refresh_token) {
    return null; // already in sync
  }

  try {
    const { data, error } = await client.auth.setSession({
      access_token: candidate.access_token,
      refresh_token: candidate.refresh_token,
    });
    if (error) {
      const msg = error.message || '';
      if (!/abort/i.test(msg)) {
        console.warn('[auth] adoptSharedSession failed:', msg);
      }
      return null;
    }
    return data.session ?? null;
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    if (!/abort/i.test(msg)) {
      console.warn('[auth] adoptSharedSession failed:', msg);
    }
    return null;
  }
}
